import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Atom } from "react-loading-indicators";
import './App.css';

function App() {
  // ============= STATE MANAGEMENT =============
  // Basic company information states
  const [companyName, setCompanyName] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [geography, setGeography] = useState(''); // Default empty until we get data
  const [geographyOptions, setGeographyOptions] = useState([]); // Will store countries from API
  

  // Sector selection states
  const [sectorsMapping, setSectorsMapping] = useState({});
  const [sector1, setSector1] = useState('');
  const [sector2, setSector2] = useState('');

  // Investor count states
  const [numFinancialInvestors, setNumFinancialInvestors] = useState('');
  const [numStrategicInvestors, setNumStrategicInvestors] = useState('');

  // UI state management
  const [processedData, setProcessedData] = useState({
    financialInvestors: { data: [], columns: [] },
    strategicInvestors: { data: [], columns: [] }
  });
  const [activeTab, setActiveTab] = useState('financial'); // Track active tab
  const [responseDataFlag, setResponseDataFlag] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingCrewAI, setIsExportingCrewAI] = useState(false);
  const isAnyExporting = isExporting || isExportingCrewAI;
  const [isLoadingExport, setIsLoadingExport] = useState(false);

  // Add new state for contact information modal
  const [showContactModal, setShowContactModal] = useState(false);
  const [showContactModalCrewAI, setShowContactModalCrewAI] = useState(false);
  const [contactData, setContactData] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [alternativeUrl, setAlternativeUrl] = useState('');
  const [exportError, setExportError] = useState(null);

  // ============= EFFECTS AND DATA FETCHING =============
  // Fetch sector data on component mount
  useEffect(() => {
    axios.get('/api/fetch_sectors')
      .then((response) => {
        setSectorsMapping(response.data);
        // Set default values for sector dropdowns
        const firstSector1Key = Object.keys(response.data)[0];
        setSector1(firstSector1Key);
        setSector2(response.data[firstSector1Key][0]);
      })
      .catch((error) => {
        console.error('Error fetching sector data:', error);
      });
  }, []);

  // ============= EVENT HANDLERS =============
  // Handle changes in the first sector dropdown
  const handleSector1Change = (e) => {
    const selectedSector1 = e.target.value;
    setSector1(selectedSector1);
    // Reset sector2 to first available option in the new sector1 category
    setSector2(sectorsMapping[selectedSector1]?.[0] || '');
  };

  // Fetch geography data on component mount
  useEffect(() => {
    axios.get('/api/fetch_geo_data')
      .then((response) => {
        // Extract and sort unique countries
        const countries = [...new Set(response.data.countries)].sort();
        setGeographyOptions(countries);
        
        // Set default geography to first country in the list if available
        if (countries.length > 0) {
          setGeography(countries[0]);
        }
      })
      .catch((error) => {
        console.error('Error fetching geography data:', error);
        // Fallback options in case API fails
        const fallbackCountries = ['None'];
        setGeographyOptions(fallbackCountries);
        setGeography(fallbackCountries[0]);
      });
  }, []);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = {
      companyName,
      companyDescription,
      websiteUrl,
      geography,
      sector1,
      sector2,
      numFinancialInvestors,
      numStrategicInvestors,
    };

    axios.post('/api/submit', formData)
      .then((response) => {
        setProcessedData(response.data);
        setIsLoading(false);
        setSubmitted(true);
        setResponseDataFlag(true);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('Error submitting form:', error);
      });
  };

  // Render the container to hold the results
  const renderResultsContainer = () => {

    return(
    <div style={styles.resultsContainer}>
      <div style={styles.tabsContainer}>
        <button 
          style={{
            ...styles.tabButton,
            ...(activeTab === 'financial' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('financial')}
        >
          Financial Investors ({processedData.financialInvestors.data.length})
        </button>
        <button 
          style={{
            ...styles.tabButton,
            ...(activeTab === 'strategic' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('strategic')}
        >
          Strategic Investors ({processedData.strategicInvestors.data.length})
        </button>
      </div>

      <div style={styles.tableWrapper}>
        {activeTab === 'financial' && (
          <>
            <h3 style={styles.tableTitleText}>Financial Investors</h3>
            {renderTable(
              processedData.financialInvestors.data,
              processedData.financialInvestors.columns
            )}
          </>
        )}
        
        {activeTab === 'strategic' && (
          <>
            <h3 style={styles.tableTitleText}>Strategic Investors</h3>
            {renderTable(
              processedData.strategicInvestors.data,
              processedData.strategicInvestors.columns
            )}
          </>
        )}

        <div style={styles.exportButtonContainer}>
          {isLoadingExport && (
            <div style={styles.loadingIndicator}>
              <Atom color="#32cd32" size="medium" text="Exporting..." />
            </div>
          )}
          <button 
            style={styles.exportButton}
            onClick={exportData}
            disabled={isAnyExporting}
          >
            {isExporting ? 'Exporting...' : 'Export All Investors (Hunter)'}
          </button>
          <button 
            style={styles.exportButton}
            onClick={exportDataCrewAI}
            disabled={isAnyExporting}
          >
            {isExportingCrewAI ? 'Exporting...' : 'Export All Investors (CrewAI)'}
          </button>
        </div>
      </div>
    </div>
    );
  };

  const renderTable = (data, columns) => {
    const excludedColumns = ['Description', 'Target Revenue', 'Target EBITDA'];
    const filteredColumns = columns.filter((column) => !excludedColumns.includes(column));
  
    return (
      <table style={styles.table}>
        <thead>
          <tr>
            {filteredColumns.map((column) => (
              <th key={column} style={styles.tableHeader}>{column}</th>
            ))}
            <th style={styles.tableHeader}>Contacts</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {filteredColumns.map((column) => (
                <td key={`${rowIndex}-${column}`} style={styles.tableCell}>
                  {column === 'Similarity Score' 
                    ? `${Math.round(row[column])}%`
                    : row[column]}
                </td>
              ))}
              <td style={styles.tableCell}>
                <button
                  onClick={() => fetchContactInfo(row['Investor Name'], row['Investor Website'])}
                  style={styles.actionButton}
                >
                  Hunter
                </button>
                <button
                  onClick={() => fetchContactInfo_crewAI(row['Investor Name'], row['Investor Website'])}
                  style={{ ...styles.actionButton, marginTop: '8px' }}
                >
                  CrewAI
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };  

  // Function to fetch contact information from Hunter
  const fetchContactInfo = async (investorName, investorWebsite) => {
    setLoadingContacts(true);
    setSelectedInvestor(investorName);
    setShowContactModal(true);
    setExportError(null);
    
    try {
      const response = await axios.post('/api/fetch_buyer_emails', {
        investorWebsite: investorWebsite,
        investorName: investorName
      });
      
      if (response.data && response.data.length > 0) {
        setContactData(response.data);
      } else {
        setContactData(null);
      }
    } catch (error) {
      console.error('Error fetching contact information:', error);
      setContactData(null);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Function to fetch contact information from CrewAI
  const fetchContactInfo_crewAI = async (investorName, investorWebsite) => {
    setLoadingContacts(true);
    setSelectedInvestor(investorName);
    setShowContactModalCrewAI(true);
    setExportError(null);
    
    try {
      const response = await axios.post('/api/fetch_buyer_emails_crew_ai', {
        investorWebsite: investorWebsite,
        investorName: investorName
      });
      
      if (response.data && response.data.length > 0) {
        setContactData(response.data);
      } else {
        setContactData(null);
      }
    } catch (error) {
      console.error('Error fetching contact information:', error);
      setContactData(null);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Function to retry with alternative URL
  const handleRetryWithUrl = async (e) => {
    e.preventDefault();
    if (!alternativeUrl) return;
    
    await fetchContactInfo(selectedInvestor, alternativeUrl);
    setAlternativeUrl('');
  };

  // Modal component for contact information (Hunter)
  const ContactModal = () => (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h3>Contact Information - {selectedInvestor}</h3>
          <button 
            onClick={() => setShowContactModal(false)}
            style={styles.closeButton}
          >
            ×
          </button>
        </div>
        
        {loadingContacts ? (
          <div style={styles.loadingContainer}>
            <Atom color="#32cd32" size="medium" text="Loading contacts..." />
          </div>
        ) : contactData ? (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Name</th>
                  <th style={styles.tableHeader}>Email</th>
                  <th style={styles.tableHeader}>Position</th>
                  <th style={styles.tableHeader}>Department</th>
                  <th style={styles.tableHeader}>Seniority</th>
                  <th style={styles.tableHeader}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {contactData.map((contact, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>
                      {`${contact.first_name || ''} ${contact.last_name || ''}`}
                    </td>
                    <td style={styles.tableCell}>{contact.email}</td>
                    <td style={styles.tableCell}>{contact.position || '-'}</td>
                    <td style={styles.tableCell}>{contact.department || '-'}</td>
                    <td style={styles.tableCell}>{contact.seniority || '-'}</td>
                    <td style={styles.tableCell}>
                      {contact.confidence ? `${contact.confidence}%` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.exportContainer}>
              {exportError && (
                <div style={styles.errorMessage}>{exportError}</div>
              )}
              <button
                onClick={exportContacts}
                disabled={isExporting}
                style={styles.exportButton}
              >
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </button>
            </div>
          </>
        ) : (
          <div style={styles.noDataContainer}>
            <p style={styles.noDataMessage}>
              No contact information available for this investor. Try inputting a new business url:
            </p>
            <form onSubmit={handleRetryWithUrl} style={styles.retryForm}>
              <div style={styles.retryInputContainer}>
                <input
                  type="url"
                  value={alternativeUrl}
                  onChange={(e) => setAlternativeUrl(e.target.value)}
                  placeholder="Enter alternative website URL"
                  style={styles.retryInput}
                  required
                />
                <button type="submit" style={styles.retryButton}>
                  Retry
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );

  // Modal component for contact information (CrewAI)
  const ContactModalCrewAI = () => (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h3>Contact Information - {selectedInvestor}</h3>
          <button 
            onClick={() => setShowContactModalCrewAI(false)}
            style={styles.closeButton}
          >
            ×
          </button>
        </div>
        
        {loadingContacts ? (
          <div style={styles.loadingContainer}>
            <Atom color="#32cd32" size="medium" text="Loading contacts..." />
          </div>
        ) : contactData ? (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Name</th>
                  <th style={styles.tableHeader}>Position</th>
                  <th style={styles.tableHeader}>Department</th>
                  <th style={styles.tableHeader}>Email</th>
                  <th style={styles.tableHeader}>Linkedin</th>
                </tr>
              </thead>
              <tbody>
                {contactData.map((contact, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>{contact.name || '-'}</td>
                    <td style={styles.tableCell}>{contact.position || '-'}</td>
                    <td style={styles.tableCell}>{contact.department || '-'}</td>
                    <td style={styles.tableCell}>{contact.email || '-'}</td>
                    <td style={styles.tableCell}>{contact.linkedin || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.exportContainer}>
              {exportError && (
                <div style={styles.errorMessage}>{exportError}</div>
              )}
              <button
                onClick={exportContacts}
                disabled={isExporting}
                style={styles.exportButton}
              >
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </button>
            </div>
          </>
        ) : (
          <div style={styles.noDataContainer}>
            <p style={styles.noDataMessage}>
              No contact information available for this investor.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Handle data export to Excel (Hunter)
  const exportData = async () => {
    setIsExporting(true);
    try {
      // Log the data being sent
      console.log('Sending data:', {
        financialInvestors: processedData.financialInvestors,
        strategicInvestors: processedData.strategicInvestors
      });
  
      const response = await axios.post('/api/export_buyer_list', {
        financialInvestors: {
          data: processedData.financialInvestors.data.map(row => {
            // Create a clean object with only the data we need
            const cleanRow = {};
            processedData.financialInvestors.columns.forEach(column => {
              cleanRow[column] = row[column] || '';
            });
            return cleanRow;
          }),
          columns: processedData.financialInvestors.columns
        },
        strategicInvestors: {
          data: processedData.strategicInvestors.data.map(row => {
            const cleanRow = {};
            processedData.strategicInvestors.columns.forEach(column => {
              cleanRow[column] = row[column] || '';
            });
            return cleanRow;
          }),
          columns: processedData.strategicInvestors.columns
        }
      }, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      // Check if the response is actually an error message
      if (response.data.type === 'application/json') {
        // Convert blob to text to read error message
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.error || 'Export failed');
      }
  
      // Create blob and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Investor_Details.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
  
    } catch (error) {
      console.error('Error exporting data:', error);
      alert(error.message || 'Failed to export investor details. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle data export to Excel (CrewAI)
  const exportDataCrewAI = async () => {
    setIsExportingCrewAI(true);
    try {
      // Log the data being sent
      console.log('Sending data:', {
        financialInvestors: processedData.financialInvestors,
        strategicInvestors: processedData.strategicInvestors
      });
  
      const response = await axios.post('/api/export_buyer_list_crew_ai', {
        financialInvestors: {
          data: processedData.financialInvestors.data.map(row => {
            // Create a clean object with only the data we need
            const cleanRow = {};
            processedData.financialInvestors.columns.forEach(column => {
              cleanRow[column] = row[column] || '';
            });
            return cleanRow;
          }),
          columns: processedData.financialInvestors.columns
        },
        strategicInvestors: {
          data: processedData.strategicInvestors.data.map(row => {
            const cleanRow = {};
            processedData.strategicInvestors.columns.forEach(column => {
              cleanRow[column] = row[column] || '';
            });
            return cleanRow;
          }),
          columns: processedData.strategicInvestors.columns
        }
      }, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      // Check if the response is actually an error message
      if (response.data.type === 'application/json') {
        // Convert blob to text to read error message
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.error || 'Export failed');
      }
  
      // Create blob and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Investor_Details.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
  
    } catch (error) {
      console.error('Error exporting data:', error);
      alert(error.message || 'Failed to export investor details. Please try again.');
    } finally {
      setIsExportingCrewAI(false);
    }
  };

  // Function to export contact data to Excel
  const exportContacts = async () => {
    if (!contactData) return;
    
    setIsExporting(true);
    setExportError(null);
    
    try {
      const response = await axios.post('/api/export_buyer_emails', contactData, {
        responseType: 'blob'
      });

      // Create and trigger download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Buyer_Emails.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting contacts:', error);
      setExportError('Failed to export contacts. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };


  // ============= COMPONENT RENDER =============
  return (
    <div style={styles.appContainer(submitted)}>
      {/* Form Container - Shrinks to side when submitted */}
      <div style={styles.formContainer(submitted)}>
        <form onSubmit={handleSubmit} style={styles.form}>
        <h3 style={styles.headerText}>General Business Information</h3>
        
        {/* Company Basic Information Section */}
        <p style={styles.descriptionText}>Business Name:</p>
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Enter Business Name"
          required
          style={styles.input}
        />

        <p style={styles.descriptionText}>Website URL:</p>
        <input
          type="url"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="Enter Website URL"
          style={styles.input}
        />

        <p style={styles.descriptionText}>Business Description:</p>
        <textarea
          value={companyDescription}
          onChange={(e) => setCompanyDescription(e.target.value)}
          placeholder="Enter Business Description (Optional)"
          style={styles.textArea}
        />

        {/* Geography Dropdown */}
        <p style={styles.descriptionText}>Business Geography:</p>
        <div style={styles.selectContainer}>
          <select 
            value={geography} 
            style={styles.select} 
            onChange={(e) => setGeography(e.target.value)}
            required
          >
            <option value="">Select a country</option>
            {geographyOptions.map((geo) => (
              <option key={geo} value={geo}>{geo}</option>
            ))}
          </select>
          <div style={styles.selectArrow}></div>
        </div>

        {/* Sector Selection Section */}
        <p style={styles.descriptionText}>Sector 1st Level</p>
        <div style={styles.selectContainer}>
          <select value={sector1} style={styles.select} onChange={handleSector1Change}>
            {Object.keys(sectorsMapping).map((key) => (
              <option key={key} value={key}>{key}</option>
            ))}
          </select>
          <div style={styles.selectArrow}></div>
        </div>

        <p style={styles.descriptionText}>Sector 2nd Level</p>
        <div style={styles.selectContainer}>
          <select 
            value={sector2} 
            style={styles.select} 
            onChange={(e) => setSector2(e.target.value)} 
            disabled={!sector1}
          >
            {sector1 && sectorsMapping[sector1]?.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <div style={styles.selectArrow}></div>
        </div>
        
        {/* Investor Numbers Section */}
        <p style={styles.descriptionText}>Number of Financial Investors</p>
        <input
          type="number"
          value={numFinancialInvestors}
          onChange={(e) => setNumFinancialInvestors(e.target.value)}
          placeholder="Enter number of financial investors"
          required
          style={styles.input}
        />

        <p style={styles.descriptionText}>Number of Strategic Investors</p>
        <input
          type="number"
          value={numStrategicInvestors}
          onChange={(e) => setNumStrategicInvestors(e.target.value)}
          placeholder="Enter number of strategic investors"
          required
          style={styles.input}
        />

        {/* Loading Indicator and Submit Button */}
        {isLoading && <div align="center"><Atom color="#32cd32" size="medium" text="Processing..." /></div>}
        <button type="submit" disabled={isLoading} style={styles.button}>Submit</button>
        </form>
      </div>
      
      {/* Results Table Section - Appears after form submission */}
      {responseDataFlag && renderResultsContainer()}
      {/* Contact Modal Section - Appears when user requests to see contacts */}
      {showContactModal && <ContactModal />}
      {!showContactModal && showContactModalCrewAI && <ContactModalCrewAI />}
    </div>
  );
}

// ============= STYLES =============
const styles = {
  pageContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '10vh', // Start 10% down the page
  },
  scrollContainer: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    justifyContent: 'center',
  },
  appContainer: (submitted) => ({
    display: 'flex',
    justifyContent: submitted ? 'flex-start' : 'center',
    width: '100%',
    maxWidth: '100%',
    padding: '10px',
  }),
  formContainer: (submitted) => ({
    width: submitted ? '20%' : '100%',
    maxWidth: submitted ? 'none' : '400px',
    transition: 'all 0.5s ease',
    padding: '20px',
  }),
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  descriptionText: {
    fontFamily: 'Arial, sans-serif',
    margin: '1px 0',
    fontSize: '12px',
  },
  valuationText: {
    fontFamily: 'Arial, sans-serif',
    margin: '1px 0',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '20px',
    borderTop: '1px solid black',
    //borderBottom: '1px solid black',
    //backgroundColor: '#e6f2ff',
  },
  headerText: {
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    marginTop: '10px',
    marginBottom: '20px',
    borderBottom: '1px solid black',
  },
  input: {
    margin: '5px 0',
    padding: '7px',
    borderRadius: '4px',
    border: '1px solid #cccccc',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
  },
  textArea: {
    margin: '10px 0',
    padding: '10px',
    height: '100px',
    resize: 'vertical',
    borderRadius: '4px',
    border: '1px solid #cccccc',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
  },
  barChartStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '400px', // Adjust this value as needed
    width: '100%',
  },
  button: {
    margin: '10px 0',
    padding: '10px',
    backgroundColor: '#6487dc',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '4px',
  },
  tableContainer: {
    width: '75%',
    padding: '20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '10px',
  },
  tableHeader: {
    backgroundColor: '#e6f2ff',
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  tableCell: {
    padding: '12px',
    borderBottom: '1px solid #ddd',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
  },
  tableCellNumeric: {
    padding: '3px',
    borderBottom: '1px solid #ddd',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
    textAlign: 'right',
  },
  tableCellMultiple: {
    padding: '3px',
    borderBottom: '1px solid #ddd',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
    textAlign: 'center',
  },
  tableTitleText: {
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    marginTop: '0',
    marginBottom: '10px',
    borderBottom: '1px solid black',
  },
  sliderContainer: {
    margin: '10px 0',
  },
  slider: {
    width: '100%',
    margin: '10px 0',
  },
  sliderLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
  },
  selectContainer: {
    position: 'relative',
    margin: '10px 0',
  },
  select: {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    width: '100%',
    padding: '10px',
    paddingRight: '30px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
    border: '1px solid #cccccc',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
  },
  selectArrow: {
    position: 'absolute',
    top: '50%',
    right: '10px',
    transform: 'translateY(-50%)',
    width: '0',
    height: '0',
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
    borderTop: '5px solid #333',
    pointerEvents: 'none',
  },
  resultsContainer: {
    width: '80%',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
  },
  resultsTitleText: {
    fontFamily: 'Arial, sans-serif',
    fontSize: '20px',
    marginTop: '0px',
    marginBottom: '3px',
    textAlign: 'center',
  },
  tabsContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
  },
  tabButton: {
    padding: '10px',
    border: '1px solid #6487dc',
    backgroundColor: 'white',
    color: '#6487dc',
    cursor: 'pointer',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'Arial, sans-serif',
    minWidth: '120px',
    transition: 'all 0.3s ease',
  },
  activeTab: {
    backgroundColor: '#6487dc',
    color: 'white',
  },
  tableWrapper: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '4px',
    width: '100%',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px',
  },
  tableHeader: {
    backgroundColor: '#e6f2ff',
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '4px',
    width: '80%',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '5px',
  },
  actionButton: {
    padding: '5px 10px',
    backgroundColor: '#6487dc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
  },
  noDataMessage: {
    textAlign: 'center',
    padding: '20px',
    color: '#666',
  },
  exportContainer: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
  },
  exportButton: {
    padding: '10px',
    backgroundColor: '#6487dc',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'Arial, sans-serif',
    minWidth: '120px',
    transition: 'background-color 0.3s',
  },
  errorMessage: {
    color: '#f44336',
    fontSize: '14px',
  },
  noDataContainer: {
    padding: '20px',
  },
  retryForm: {
    marginTop: '20px',
  },
  retryText: {
    marginBottom: '10px',
    color: '#666',
  },
  retryInputContainer: {
    display: 'flex',
    gap: '10px',
  },
  retryInput: {
    flex: 1,
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '14px',
  },
  retryButton: {
    padding: '8px 16px',
    backgroundColor: '#6487dc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  exportContainer: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
  },
  exportButton: {
    padding: '10px 20px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: '#45a049',
    },
    '&:disabled': {
      backgroundColor: '#cccccc',
      cursor: 'not-allowed',
    },
  },
  loadingIndicator: {
    marginRight: '10px',
  },
  exportButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px solid #eee',
    gap: '12px',
  },
};

export default App;
